import { Burger, Group, Header, Select } from '@mantine/core'
import { useLocation } from 'react-router-dom'
import { useForceEnglish, useMantineTheme, useMediaQuery } from 'hooks'
import { useCurrentUser } from 'context/useCurrentUser'
import { ArcLogo } from 'components/ArcLogo'
import { StudentRequestsWidget } from 'pages/TeacherDashboard/components/ReviewStudentRequests'
import { TOP_BAR_HEIGHT } from './constants'
import { UserMenu } from './UserMenu'
import { useSchoolSelect } from './useSchoolSelect'

const TOP_BAR_Z_INDEX = 99
/** ArcLogo SVG is 157x30 — 28px keeps the proportion without stretching */
const LOGO_HEIGHT_PX = 28
const SCHOOL_SELECT_WIDTH = 260
/** 44px touch target for the burger, matching the sidebar items */
const BURGER_BOX = 44
/**
 * (rail 60 - burger box 44) / 2: the burger centers on the same vertical
 * axis (x = 30) as the collapsed sidebar icons below it.
 */
const LEFT_PADDING = 8
const RIGHT_PADDING = 16

interface ArcTopBarProps {
  /**
   * Webview mode (?app=true): the bar stays mounted so its side effects
   * (forced English, school auto-select) keep running, but renders nothing —
   * same contract as the legacy Navbar container with display: none.
   */
  readonly hidden: boolean
  readonly sidebarCollapsed: boolean
  readonly onToggleSidebar: () => void
}

/**
 * Arc-only fixed top bar (AR-578): burger toggling the sidebar + ArcLogo on
 * the left; school selector, student requests widget and the Mantine user
 * menu on the right. Rendered by the persistent ArcShellLayout (or by Layout
 * as a fallback) instead of the legacy Navbar when `arc-mosaico-project` is on.
 */
export const ArcTopBar = ({ hidden, sidebarCollapsed, onToggleSidebar }: ArcTopBarProps) => {
  const theme = useMantineTheme()
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`)
  const { currentUser } = useCurrentUser()
  const school = useSchoolSelect()
  // No polling widget on the error screen (the session may already be dead)
  const isErrorRoute = useLocation().pathname === '/error_permission'
  // Arc forces English — the legacy NavbarMenu (which owned this) is not
  // rendered on the Arc path anymore
  useForceEnglish()

  const isTutorOrManager = currentUser?.profile_used === 'tutor' || currentUser?.profile_used === 'manager'

  if (hidden) return null

  return (
    <Header
      height={TOP_BAR_HEIGHT}
      fixed
      position={{ top: 0, left: 0, right: 0 }}
      zIndex={TOP_BAR_Z_INDEX}
      withBorder
      sx={{
        display: 'flex',
        alignItems: 'center',
        paddingLeft: LEFT_PADDING,
        paddingRight: RIGHT_PADDING,
        backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[8] : theme.white,
      }}
    >
      <Group position='apart' noWrap sx={{ flex: 1 }}>
        <Group spacing={16} noWrap>
          <Burger
            opened={!sidebarCollapsed}
            onClick={onToggleSidebar}
            size='sm'
            aria-label='Toggle navigation'
            sx={{
              width: BURGER_BOX,
              height: BURGER_BOX,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 8,
              transition: 'background-color 150ms ease',
              '&:hover': {
                backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
              },
            }}
          />
          <ArcLogo height={LOGO_HEIGHT_PX} />
        </Group>
        <Group spacing={8} noWrap>
          {!isMobile && school.visible && (
            <Select
              data={school.data}
              value={school.value}
              onChange={school.onChange}
              withinPortal
              size='md'
              searchable
              sx={{ width: SCHOOL_SELECT_WIDTH }}
            />
          )}
          {!isMobile && isTutorOrManager && !isErrorRoute && <StudentRequestsWidget />}
          <UserMenu />
        </Group>
      </Group>
    </Header>
  )
}
